/**
 * Cache em memória compartilhado entre listagens (properties, condominiums, blogPosts).
 * Persiste enquanto a aba estiver aberta; TTL evita dados eternamente velhos.
 */

const DEFAULT_TTL_MS = 5 * 60_000;

const store = new Map();

function entryKey(namespace, scopeKey = '') {
  return `${namespace}:${scopeKey || 'all'}`;
}

function isFresh(fetchedAt, ttlMs) {
  return fetchedAt > 0 && Date.now() - fetchedAt < ttlMs;
}

export function getCachedCatalog(namespace, scopeKey = '', ttlMs = DEFAULT_TTL_MS) {
  const entry = store.get(entryKey(namespace, scopeKey));
  if (!entry?.data) return null;
  return {
    data: entry.data,
    stale: !isFresh(entry.fetchedAt, ttlMs),
    fetchedAt: entry.fetchedAt,
  };
}

export function setCachedCatalog(namespace, scopeKey, data) {
  store.set(entryKey(namespace, scopeKey), {
    data,
    fetchedAt: Date.now(),
    promise: null,
  });
}

export function invalidateCatalog(namespace, scopeKey) {
  if (scopeKey != null && scopeKey !== '') {
    store.delete(entryKey(namespace, scopeKey));
    return;
  }
  const prefix = `${namespace}:`;
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
}

/**
 * Busca com cache + dedupe de promise em voo.
 * @param {string} namespace
 * @param {string} scopeKey
 * @param {() => Promise<any>} fetchFn
 * @param {{ force?: boolean, ttlMs?: number }} [options]
 */
export async function fetchCachedCatalog(namespace, scopeKey, fetchFn, options = {}) {
  const { force = false, ttlMs = DEFAULT_TTL_MS } = options;
  const key = entryKey(namespace, scopeKey);
  const existing = store.get(key);

  if (!force) {
    if (existing?.data && isFresh(existing.fetchedAt, ttlMs)) {
      return existing.data;
    }
    if (existing?.promise) {
      return existing.promise;
    }
  }

  const promise = (async () => {
    try {
      const data = await fetchFn();
      store.set(key, { data, fetchedAt: Date.now(), promise: null });
      return data;
    } catch (err) {
      const current = store.get(key);
      if (current?.promise === promise) {
        store.set(key, { ...current, promise: null });
      }
      throw err;
    }
  })();

  store.set(key, {
    data: existing?.data ?? null,
    fetchedAt: existing?.fetchedAt ?? 0,
    promise,
  });

  return promise;
}

export const CATALOG_NS = {
  PROPERTIES: 'properties',
  CONDOMINIUMS: 'condominiums',
  BLOG_POSTS: 'blogPosts',
};

export const CATALOG_TTL_MS = DEFAULT_TTL_MS;
